/**
 * Time — SPEC §3.6.
 *
 * **Every stamp is Taipei, and says so**: `11:20 Taipei`. Times arrive as ISO with `+08:00` (never
 * a bare `Z`) from `utils/events.plain`, which means the characters at positions 11–16 of the
 * string ARE the Taipei reading. So this file slices rather than converts: no timezone database, no
 * `Intl` round trip, and no chance of the viewer's own zone leaking into a label.
 *
 * **Ages are never computed from the viewer's clock.** A laptop running slow would turn the strip
 * amber; a laptop running fast would hide a dead watcher. Every age comes from the server's `now`
 * in the `heartbeat` payload, which is why `age()` takes it as an argument and there is no
 * `Date.now()` anywhere below.
 *
 * The viewer's local reading is **hover only**, via the mockups' own mechanism: a
 * `data-taipei="2026-09-29T11:47"` attribute and a `.tip__local` filled from
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`. Two zones on every stamp is a page about
 * time zones.
 */

export const ZONE = "Taipei";

const WEEKDAY: Record<"en" | "zh-Hant", string[]> = {
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  "zh-Hant": ["週日", "週一", "週二", "週三", "週四", "週五", "週六"],
};

const MONTH: Record<"en" | "zh-Hant", string[]> = {
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  "zh-Hant": ["1 月", "2 月", "3 月", "4 月", "5 月", "6 月", "7 月", "8 月", "9 月", "10 月", "11 月", "12 月"],
};

/** The date part of an ISO stamp, as Taipei already wrote it. */
export const dateOf = (iso: string): string => iso.slice(0, 10);

/** `11:20` — the clock, with no zone word. Use `hm` for anything a reader sees. */
export const clock = (iso: string): string => iso.slice(11, 16);

/** `11:20 Taipei` — the ground rule, on every stamp the console draws itself. */
export const hm = (iso: string): string => `${clock(iso)} ${ZONE}`;

function weekdayIndex(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** `Thu 24 Sep` — `render.day()`'s shape, and the frontend's for any date it formats itself. */
export function dayLabel(day: string, lang: "en" | "zh-Hant" = "en"): string {
  const iso = dateOf(day);
  const [, m, d] = iso.split("-").map(Number);
  const wd = WEEKDAY[lang][weekdayIndex(iso)];
  return lang === "zh-Hant" ? `${m} 月 ${d} 日 ${wd}` : `${wd} ${d} ${MONTH.en[m - 1]}`;
}

/**
 * `11:20 Taipei` today; `Thu 11:20 Taipei` any other day — `render.when()`'s rule, and the
 * frontend's too. **A bare `%H:%M` from another day always carries its weekday**, because a bare
 * time from yesterday reads as this morning's.
 */
export function when(iso: string, nowIso: string, lang: "en" | "zh-Hant" = "en"): string {
  if (!iso) return "—";
  if (dateOf(iso) === dateOf(nowIso)) return hm(iso);
  return `${WEEKDAY[lang][weekdayIndex(dateOf(iso))]} ${clock(iso)} ${ZONE}`;
}

/** Seconds between two stamps, from the SERVER's `now`. Both carry their offset, so this is
 *  absolute and the viewer's zone does not enter it. */
export function age(nowIso: string, thenIso: string | null): number | null {
  if (!thenIso) return null;
  const now = Date.parse(nowIso);
  const then = Date.parse(thenIso);
  if (Number.isNaN(now) || Number.isNaN(then)) return null;
  return Math.max(0, Math.round((now - then) / 1000));
}

/** `45 s` · `4 m` · `2 h 05 m` — `render.span()`'s shape. */
export function span(seconds: number | null): string {
  if (seconds === null) return "—";
  const s = Math.max(0, Math.trunc(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.trunc(s / 60);
  if (m < 60) return `${m} m`;
  return `${Math.trunc(m / 60)} h ${String(m % 60).padStart(2, "0")} m`;
}

/** The viewer's own zone name, for the hover reading only. */
export function viewerZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "your device";
  } catch {
    return "your device";
  }
}

/**
 * `localReadings()`, ported from boot.js: what the hover says, built from the browser's real zone
 * rather than drawn as a guess. Returns `null` when the stamp does not parse, so the tooltip simply
 * has nothing extra to say.
 */
export function localReading(iso: string): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const time = at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const day = at.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  return `${time} ${day} · ${viewerZone()}`;
}

/** Minutes since midnight Taipei, for placing a mark on D1's frame. */
export function minutesOfDay(iso: string): number {
  const [h, m] = clock(iso).split(":").map(Number);
  return h * 60 + m;
}
